<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import Swal from "sweetalert2";
import api from "../../api/axios";
import ArgonButton from "@/components/ArgonButton.vue";

const route = useRoute();

const conversations = ref([]);
const activeConversation = ref(null);
const messages = ref([]);
const nuevoMensaje = ref("");
const enviando = ref(false);
const quickReplies = ref([]);
const showQuickReplies = ref(false);
const showQuickReplyAdmin = ref(false);
const nuevaRespuestaRapida = ref({ shortcut: "", body: "" });
let intervalo = null;

async function cargarConversaciones() {
  const res = await api.get("/communication/conversations");
  conversations.value = res.data.conversations;
}

async function cargarRespuestasRapidas() {
  const res = await api.get("/communication/quick-replies");
  quickReplies.value = res.data.replies;
}

function usarRespuestaRapida(reply) {
  nuevoMensaje.value = reply.body;
  showQuickReplies.value = false;
}

async function crearRespuestaRapida() {
  if (!nuevaRespuestaRapida.value.shortcut.trim() || !nuevaRespuestaRapida.value.body.trim()) return;

  try {
    await api.post("/communication/quick-replies", nuevaRespuestaRapida.value);
    nuevaRespuestaRapida.value = { shortcut: "", body: "" };
    await cargarRespuestasRapidas();
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo crear la respuesta rápida",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  }
}

async function eliminarRespuestaRapida(reply) {
  await api.delete(`/communication/quick-replies/${reply.id}`);
  await cargarRespuestasRapidas();
}

async function cargarMensajes() {
  if (!activeConversation.value) return;
  const res = await api.get(`/communication/conversations/${activeConversation.value.id}/messages`);
  messages.value = res.data.messages;
}

async function abrirConversacion(conv) {
  activeConversation.value = conv;
  await cargarMensajes();
}

async function alternarPausaIA() {
  if (!activeConversation.value) return;

  const pausar = !activeConversation.value.ai_paused;

  try {
    const res = await api.patch(`/communication/conversations/${activeConversation.value.id}/ai-pause`, { paused: pausar });
    activeConversation.value.ai_paused = res.data.conversation.ai_paused;

    const conv = conversations.value.find(c => c.id === activeConversation.value.id);
    if (conv) conv.ai_paused = res.data.conversation.ai_paused;

  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo actualizar la conversación",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  }
}

function volverALaLista() {
  activeConversation.value = null;
}

async function enviar() {
  if (!nuevoMensaje.value.trim() || !activeConversation.value) return;

  enviando.value = true;

  try {
    await api.post(`/communication/conversations/${activeConversation.value.id}/messages`, { body: nuevoMensaje.value });
    nuevoMensaje.value = "";
    await cargarMensajes();
    await cargarConversaciones();

  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo enviar el mensaje",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    enviando.value = false;
  }
}

function formatFecha(fecha) {
  return new Date(fecha).toLocaleString("es-CO");
}

function iconoCanal(channel) {
  if (channel === "whatsapp") return "bi-whatsapp text-success";
  if (channel === "messenger") return "bi-messenger text-primary";
  return "bi-instagram text-danger";
}

function estadoMensaje(status) {
  return {
    queued: "Pendiente de envío",
    sent: "Enviado",
    delivered: "Entregado",
    read: "Leído",
    failed: "No se pudo enviar"
  }[status] || status;
}

onMounted(async () => {
  await cargarConversaciones();
  await cargarRespuestasRapidas();

  // Si venimos desde el botón "Iniciar chat" de la ficha de contacto.
  const { contactId, channel } = route.query;

  if (contactId && channel) {
    const res = await api.post(`/communication/contacts/${contactId}/conversations`, { channel });
    await cargarConversaciones();

    const conv = conversations.value.find(c => c.id === res.data.conversation.id) || res.data.conversation;
    await abrirConversacion(conv);
  }

  // Refresco por polling, mismo estilo simple que esperarJob() en useVuelos.js.
  intervalo = setInterval(async () => {
    await cargarConversaciones();
    if (activeConversation.value) await cargarMensajes();
  }, 5000);
});

onUnmounted(() => {
  if (intervalo) clearInterval(intervalo);
});
</script>

<template>
  <div class="container-fluid py-4">
    <h4 class="mb-3 inbox-header-title">
      <i class="bi bi-chat-dots me-2"></i>
      Chats
    </h4>

    <div class="card inbox-card">
      <div class="row g-0 h-100">

        <!-- LISTA DE CONVERSACIONES: en móvil se oculta al abrir un chat, en
             md+ ambos paneles conviven lado a lado como siempre. -->
        <div class="col-md-4 border-end inbox-list" :class="{ 'd-none d-md-block': activeConversation }">
          <div v-if="conversations.length === 0" class="text-muted p-3">
            No hay conversaciones todavía.
          </div>

          <div
            v-for="conv in conversations"
            :key="conv.id"
            class="inbox-item p-3 border-bottom"
            :class="{ active: activeConversation?.id === conv.id }"
            @click="abrirConversacion(conv)"
          >
            <div class="d-flex justify-content-between">
              <strong>{{ conv.contact_name }}</strong>
              <i class="bi" :class="iconoCanal(conv.channel)"></i>
            </div>
            <div class="text-muted small text-truncate">{{ conv.last_message || "Sin mensajes" }}</div>
          </div>
        </div>

        <!-- THREAD: en móvil ocupa toda la pantalla solo cuando hay un chat
             abierto (con botón de volver); en md+ siempre se ve. -->
        <div class="col-md-8 flex-column inbox-thread" :class="activeConversation ? 'd-flex' : 'd-none d-md-flex'">
          <div v-if="!activeConversation" class="text-muted p-4 text-center">
            Selecciona una conversación para ver los mensajes.
          </div>

          <template v-else>
            <div class="p-3 border-bottom d-flex align-items-center gap-2">
              <button class="btn btn-sm btn-light d-md-none" @click="volverALaLista">
                <i class="bi bi-arrow-left"></i>
              </button>
              <strong>{{ activeConversation.contact_name }}</strong>
              <i class="bi ms-2" :class="iconoCanal(activeConversation.channel)"></i>

              <button
                class="btn btn-sm ms-auto"
                :class="activeConversation.ai_paused ? 'btn-outline-success' : 'btn-outline-secondary'"
                @click="alternarPausaIA"
              >
                <i class="bi" :class="activeConversation.ai_paused ? 'bi-robot' : 'bi-person-check'"></i>
                {{ activeConversation.ai_paused ? "Devolver al bot" : "Tomar conversación" }}
              </button>
            </div>

            <div class="inbox-messages p-3 flex-grow-1">
              <div
                v-for="msg in messages"
                :key="msg.id"
                class="mb-2 d-flex"
                :class="msg.direction === 'outbound' ? 'justify-content-end' : 'justify-content-start'"
              >
                <div
                  class="p-2 rounded"
                  :class="msg.direction === 'outbound' ? 'bg-success-subtle' : 'bg-light'"
                  style="max-width: 70%;"
                >
                  <div>
                    <span v-if="msg.is_ai_generated" class="badge bg-gradient-info text-dark me-1" style="font-size: 0.65rem;">
                      <i class="bi bi-robot"></i> IA
                    </span>
                    {{ msg.body }}
                  </div>
                  <div class="text-muted" style="font-size: 0.7rem;">
                    {{ formatFecha(msg.createdAt) }}
                    <span v-if="msg.direction === 'outbound'"> · {{ estadoMensaje(msg.status) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="p-3 border-top position-relative">
              <div class="d-flex gap-2">
                <button class="btn btn-outline-secondary" title="Respuestas rápidas" @click="showQuickReplies = !showQuickReplies">
                  <i class="bi bi-lightning-charge"></i>
                </button>
                <input
                  v-model="nuevoMensaje"
                  type="text"
                  class="form-control"
                  placeholder="Escribe un mensaje..."
                  @keyup.enter="enviar"
                />
                <argon-button color="success" :disabled="enviando" @click="enviar">
                  <i class="bi bi-send"></i>
                </argon-button>
              </div>

              <div v-if="showQuickReplies" class="card position-absolute quick-replies-panel">
                <div class="card-body p-2">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <strong class="small">Respuestas rápidas</strong>
                    <button class="btn btn-sm btn-link p-0" @click="showQuickReplyAdmin = true; showQuickReplies = false">Gestionar</button>
                  </div>
                  <div v-if="quickReplies.length === 0" class="text-muted small">Sin respuestas rápidas creadas.</div>
                  <button
                    v-for="reply in quickReplies"
                    :key="reply.id"
                    class="dropdown-item border-radius-md small"
                    @click="usarRespuestaRapida(reply)"
                  >
                    <strong>{{ reply.shortcut }}</strong> — {{ reply.body }}
                  </button>
                </div>
              </div>
            </div>
          </template>
        </div>

      </div>
    </div>

    <!-- MODAL GESTIONAR RESPUESTAS RÁPIDAS -->
    <div v-if="showQuickReplyAdmin" class="modal fade show d-block">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5>Respuestas rápidas</h5>
            <button class="btn-close" @click="showQuickReplyAdmin = false"></button>
          </div>
          <div class="modal-body">
            <ul class="list-group mb-3">
              <li v-for="reply in quickReplies" :key="reply.id" class="list-group-item d-flex justify-content-between align-items-center">
                <span><strong>{{ reply.shortcut }}</strong> — {{ reply.body }}</span>
                <button class="btn btn-sm btn-outline-danger" @click="eliminarRespuestaRapida(reply)">
                  <i class="bi bi-trash"></i>
                </button>
              </li>
              <li v-if="quickReplies.length === 0" class="list-group-item text-muted small">Sin respuestas rápidas.</li>
            </ul>
            <div class="mb-2">
              <input v-model="nuevaRespuestaRapida.shortcut" class="form-control mb-1" placeholder="Atajo, ej. /saludo" />
              <textarea v-model="nuevaRespuestaRapida.body" class="form-control" rows="2" placeholder="Texto de la respuesta..."></textarea>
            </div>
            <argon-button color="success" class="w-100" @click="crearRespuestaRapida">
              <i class="bi bi-plus-circle me-1"></i>Agregar
            </argon-button>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showQuickReplyAdmin = false">Cerrar</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showQuickReplyAdmin" class="modal-backdrop fade show"></div>

  </div>
</template>

<style scoped>
.inbox-card {
  height: 75vh;
}

.inbox-list {
  overflow-y: auto;
  height: 75vh;
}

.inbox-item {
  cursor: pointer;
}

.inbox-item:hover {
  background: #f8f9fa;
}

.inbox-item.active {
  background: #e9f7ef;
}

.inbox-thread {
  height: 75vh;
}

.inbox-messages {
  overflow-y: auto;
}

.quick-replies-panel {
  bottom: 100%;
  left: 1rem;
  right: 1rem;
  max-height: 250px;
  overflow-y: auto;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

@media (max-width: 767.98px) {
  .inbox-card,
  .inbox-list,
  .inbox-thread {
    height: 80vh;
  }
}

.inbox-header-title {
  color: #fff !important;
}
</style>
